import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";

import { STATUS_BADGES, StatusBadge } from "@/shared/components/StatusBadge";
import {
  McpOrgCredentialRecord,
  McpServerRecord,
  McpTokenRecord,
} from "@/shared/domain/mcp";
import { ServerFavicon } from "@/shared/domain/mcp/components/ServerFavicon";
import { useDeleteMcpOrgCredential } from "@/shared/domain/mcp/hooks/useDeleteMcpOrgCredential";
import { useMcpOrgContext } from "@/shared/domain/mcp/hooks/useMcpOrgContext";
import { useSetMcpOrgContext } from "@/shared/domain/mcp/hooks/useSetMcpOrgContext";
import { Button, ConfirmDialog } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import styles from "./McpServerCard.module.css";
import { OrgTokenDialog } from "./OrgTokenDialog";

interface McpServerCardProps {
  server: McpServerRecord;
  orgId: string | undefined;
  token?: McpTokenRecord;
  /**
   * The org's token for this server, if an admin set one. Service users use
   * it, and members do whenever their personal connection is missing or fails.
   */
  orgCredential?: McpOrgCredentialRecord;
  /** The org's tokens failed to load, so whether this server has one is unknown. */
  orgCredentialUnknown?: boolean;
  currentUserId?: string;
  isOrgAdmin: boolean;
  onConnect: (serverId: string) => void;
  onDisconnect: (serverId: string) => void;
  connectLoading?: boolean;
}

export function McpServerCard({
  server,
  orgId,
  token,
  orgCredential,
  orgCredentialUnknown = false,
  currentUserId,
  isOrgAdmin,
  onConnect,
  onDisconnect,
  connectLoading,
}: McpServerCardProps) {
  const isOwnToken = token !== undefined && token.user_id === currentUserId;
  const isOwnConnected = isOwnToken;
  const isOwnExpired =
    isOwnToken &&
    (token.token_status === "expired" ||
      token.token_status === "refresh_failed");
  const isOwnValid = isOwnConnected && !isOwnExpired;
  const hasOrgToken = orgCredential !== undefined;

  const [orgTokenDialogOpen, setOrgTokenDialogOpen] = React.useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = React.useState(false);
  const deleteOrgCredential = useDeleteMcpOrgCredential(orgId);

  const closeRemoval = () => {
    setConfirmingRemoval(false);
    deleteOrgCredential.reset();
  };

  const handleRemoveOrgToken = () => {
    deleteOrgCredential.mutate(server.server_id, {
      onSuccess: () => setConfirmingRemoval(false),
    });
  };

  const { data: orgContext, isLoading: contextLoading } = useMcpOrgContext(
    server.server_id,
    orgId,
  );
  const setContextMutation = useSetMcpOrgContext(orgId);

  const [editingContext, setEditingContext] = React.useState(false);
  const [contextDraft, setContextDraft] = React.useState("");

  const savedContext = orgContext?.context ?? "";
  const hasContext = savedContext.trim().length > 0;
  // An admin writes the org's AI context before turning on either connection.
  const needsContext = isOrgAdmin && !hasContext;

  const handleEditContext = () => {
    setContextDraft(savedContext);
    setEditingContext(true);
  };

  const handleSaveContext = () => {
    setContextMutation.mutate(
      { serverId: server.server_id, context: contextDraft },
      {
        onSuccess: () => setEditingContext(false),
      },
    );
  };

  const handleCancelEdit = () => {
    setEditingContext(false);
    setContextDraft("");
  };

  return (
    <Card variant="outlined" className={styles.card}>
      <CardContent className={styles.cardContent}>
        <div className={styles.serverName}>
          <ServerFavicon
            issuerUrl={server.oauth_issuer_url}
            displayName={server.display_name}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            {server.display_name}
          </Typography>
        </div>

        <div className={styles.connectionRow}>
          <div className={styles.connectionInfo}>
            <Typography variant="subtitle2">Personal connection</Typography>
            <Typography variant="body2" color="text.secondary">
              Use your own {server.display_name} account and permissions.
            </Typography>
            {server.scopes.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Scopes: {server.scopes.join(", ")}
              </Typography>
            )}
          </div>
          <div className={styles.connectionControls}>
            {isOwnValid && <StatusBadge status={STATUS_BADGES.mcp.connected} />}
            {isOwnExpired && <StatusBadge status={STATUS_BADGES.mcp.expired} />}
            {!isOwnConnected && (
              <StatusBadge status={STATUS_BADGES.mcp.not_connected} />
            )}
            {isOwnConnected ? (
              <>
                {isOwnExpired && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onConnect(server.server_id)}
                    disabled={connectLoading}
                  >
                    Reconnect
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => onDisconnect(server.server_id)}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Tooltip
                title={needsContext ? "Add AI context before connecting" : ""}
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onConnect(server.server_id)}
                    disabled={connectLoading || needsContext}
                  >
                    Connect
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {isOwnExpired && (
          <Alert severity="warning" className={styles.alert}>
            {expiredConnectionMessage(
              server.display_name,
              hasOrgToken,
              orgCredentialUnknown,
            )}
          </Alert>
        )}

        <div className={styles.connectionRow}>
          <div className={styles.connectionInfo}>
            <Typography variant="subtitle2">Org connection</Typography>
            <Typography variant="body2" color="text.secondary">
              A token stored as an org secret. Used by service users, and by
              members whenever their personal connection is missing or fails.
              {isOrgAdmin && hasOrgToken && (
                <> Read from the secret “{orgCredential.secret_name}”.</>
              )}
            </Typography>
          </div>
          <div className={styles.connectionControls}>
            {orgCredentialUnknown && (
              <StatusBadge status={STATUS_BADGES.mcp.unknown} />
            )}
            {!orgCredentialUnknown && hasOrgToken && (
              <StatusBadge status={STATUS_BADGES.mcp.set} />
            )}
            {!orgCredentialUnknown && !hasOrgToken && (
              <StatusBadge status={STATUS_BADGES.mcp.not_set} />
            )}
            {isOrgAdmin && orgId !== undefined && !orgCredentialUnknown && (
              <>
                {hasOrgToken ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setOrgTokenDialogOpen(true)}
                  >
                    Change token
                  </Button>
                ) : (
                  <Tooltip
                    title={
                      needsContext
                        ? "Add AI context before adding the org token"
                        : ""
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setOrgTokenDialogOpen(true)}
                        disabled={needsContext}
                      >
                        Add token
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {hasOrgToken && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => setConfirmingRemoval(true)}
                  >
                    Remove
                  </Button>
                )}
                <ConfirmDialog
                  open={confirmingRemoval}
                  onOpenChange={closeRemoval}
                  title={`Remove the ${server.display_name} org token?`}
                  description={`Service users, and members without a working personal connection, lose access to ${server.display_name}. The org secret itself is kept.`}
                  confirmLabel="Remove token"
                  destructive
                  pending={deleteOrgCredential.isPending}
                  onConfirm={handleRemoveOrgToken}
                >
                  {deleteOrgCredential.isError && (
                    <Alert severity="error">
                      {userFacingErrorMessage(deleteOrgCredential.error)}
                    </Alert>
                  )}
                </ConfirmDialog>
                <OrgTokenDialog
                  orgId={orgId}
                  serverId={server.server_id}
                  serverName={server.display_name}
                  currentSecretName={orgCredential?.secret_name}
                  open={orgTokenDialogOpen}
                  onOpenChange={setOrgTokenDialogOpen}
                />
              </>
            )}
          </div>
        </div>

        {isOrgAdmin && (
          <div className={styles.contextSection}>
            <div className={styles.contextHeader}>
              <Typography variant="subtitle2">AI Context</Typography>
              {!editingContext && !contextLoading && (
                <Tooltip title={hasContext ? "Edit context" : "Add context"}>
                  <IconButton size="small" onClick={handleEditContext}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            {contextLoading && <CircularProgress size={16} />}
            {!contextLoading && editingContext && (
              <div className={styles.contextEdit}>
                <TextField
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  size="small"
                  value={contextDraft}
                  onChange={(e) => setContextDraft(e.target.value)}
                  placeholder="Tell the AI how your org uses this server (e.g. which repos to target, project conventions, preferred workflows)."
                />
                {setContextMutation.isError && (
                  <Alert severity="error">
                    Failed to save context. Please try again.
                  </Alert>
                )}
                <div className={styles.contextEditActions}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveContext}
                    disabled={
                      setContextMutation.isPending ||
                      contextDraft.trim().length === 0
                    }
                  >
                    {setContextMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            )}
            {!contextLoading && !editingContext && hasContext && (
              <Typography
                variant="body2"
                color="text.secondary"
                className={styles.contextText}
              >
                {savedContext}
              </Typography>
            )}
            {!contextLoading && !editingContext && !hasContext && (
              <Alert severity="warning" className={styles.alert}>
                No AI context set. Add context to tell the AI how your org uses
                this server before connecting or adding the org token.
              </Alert>
            )}
          </div>
        )}

        {!isOrgAdmin && hasContext && (
          <div className={styles.contextSection}>
            <Typography variant="subtitle2">AI Context</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              className={styles.contextText}
            >
              {savedContext}
            </Typography>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function expiredConnectionMessage(
  serverName: string,
  hasOrgToken: boolean,
  orgCredentialUnknown: boolean,
): string {
  if (hasOrgToken) {
    return `Your authorization has expired. Until you reconnect, ${serverName} is used with the org token.`;
  }
  if (orgCredentialUnknown) {
    return "Your authorization has expired. Please reconnect.";
  }
  return "Your authorization has expired. Please reconnect to continue using this service.";
}
